// Pacotes de créditos para compra via PIX (Tribopay)

// Interface para slots de produto (rotação para bypass de cooldown)
export interface ProductSlot {
  slotIndex: number;
  offerHash: string;
  productHash: string;
  checkoutUrl: string;
}

export interface TribopayPackage {
  id: string;
  name: string;
  credits: number;
  priceInCents: number; // Preço em centavos (ex: 3790 = R$ 37,90)
  priceDisplay: string; // Preço formatado para exibição (USD)
  originalPrice: string; // Preço original riscado para urgência
  priceBRL: string; // Preço formatado em Reais para PIX
  offerHash: string; // Hash da oferta na Tribopay (slot principal)
  productHash: string; // Hash do produto na Tribopay
  checkoutUrl: string; // URL do checkout direto da Tribopay
  popular?: boolean;
  features: string[];
  slots?: ProductSlot[]; // Slots de rotação para bypass de cooldown de 24h
}

// Número de slots por pacote (para verificação de cooldown no frontend)
export const PACKAGE_SLOT_COUNT: Record<string, number> = {
  pack_basic: 4,
  pack_pro: 4,
  pack_supreme: 4,
};

export const TRIBOPAY_PACKAGES: Record<string, TribopayPackage> = {
  BASIC: {
    id: "pack_basic",
    name: "Basic",
    credits: 100,
    priceInCents: 3790,
    priceDisplay: "7,90",
    originalPrice: "15,90",
    priceBRL: "37,90",
    offerHash: "rd84aqozoi",
    productHash: "ywzq7ywmcb",
    checkoutUrl: "https://global.tribopay.com.br/rd84aqozoi?",
    popular: false,
    features: ["NSFW Image Generation", "NSFW Video Generation with Audio and Speech", "4K 60fps Video Upscale"],
    // Slots de rotação para bypass de cooldown de 24h
    slots: [
      {
        slotIndex: 1,
        offerHash: "rd84aqozoi",
        productHash: "ywzq7ywmcb",
        checkoutUrl: "https://global.tribopay.com.br/rd84aqozoi?",
      },
      {
        slotIndex: 2,
        offerHash: "75zdufqqdp",
        productHash: "3eym8mmayx",
        checkoutUrl: "https://global.tribopay.com.br/75zdufqqdp?",
      },
      {
        slotIndex: 3,
        offerHash: "w7q7m2bieh",
        productHash: "0jsrwbzb5v",
        checkoutUrl: "https://global.tribopay.com.br/w7q7m2bieh?",
      },
      {
        slotIndex: 4,
        offerHash: "uab8pbuqs3",
        productHash: "gr2eidoxcj",
        checkoutUrl: "https://global.tribopay.com.br/uab8pbuqs3?",
      },
    ],
  },
  PRO: {
    id: "pack_pro",
    name: "Pro",
    credits: 210,
    priceInCents: 7490,
    priceDisplay: "14.90",
    originalPrice: "29,90",
    priceBRL: "74,90",
    offerHash: "zq8xxbztuk",
    productHash: "ppaxtdgn4b",
    checkoutUrl: "https://global.tribopay.com.br/zq8xxbztuk?",
    popular: false,
    features: ["NSFW Image Generation", "NSFW Video Generation with Audio and Speech", "4K 60fps Video Upscale"],
    // Slots de rotação para bypass de cooldown de 24h
    slots: [
      {
        slotIndex: 1,
        offerHash: "zq8xxbztuk",
        productHash: "ppaxtdgn4b",
        checkoutUrl: "https://global.tribopay.com.br/zq8xxbztuk?",
      },
      {
        slotIndex: 2,
        offerHash: "zui0hkzwby",
        productHash: "rxg5pbpxba",
        checkoutUrl: "https://global.tribopay.com.br/zui0hkzwby?",
      },
      {
        slotIndex: 3,
        offerHash: "bewimvh8su",
        productHash: "cataw3zvhx",
        checkoutUrl: "https://global.tribopay.com.br/bewimvh8su?",
      },
      {
        slotIndex: 4,
        offerHash: "aulha8zdnh",
        productHash: "wkba3u0qab",
        checkoutUrl: "https://global.tribopay.com.br/aulha8zdnh?",
      },
    ],
  },
  SUPREME: {
    id: "pack_supreme",
    name: "Supreme",
    credits: 560,
    priceInCents: 18990,
    priceDisplay: "34.99",
    originalPrice: "89,90",
    priceBRL: "189,90",
    offerHash: "qmazccfu2x",
    productHash: "uypwupf8jx",
    checkoutUrl: "https://global.tribopay.com.br/qmazccfu2x?",
    popular: true,
    features: [
      "NSFW Image Generation",
      "NSFW Video Generation with Audio and Speech",
      "4K 60fps Video Upscale",
      "Faster generation speeds",
      "Priority support",
    ],
    // Slots de rotação para bypass de cooldown de 24h (3 slots)
    slots: [
      {
        slotIndex: 1,
        offerHash: "qmazccfu2x",
        productHash: "uypwupf8jx",
        checkoutUrl: "https://global.tribopay.com.br/qmazccfu2x?",
      },
      {
        slotIndex: 2,
        offerHash: "ldglsnwvdt",
        productHash: "bo2s5omikd",
        checkoutUrl: "https://global.tribopay.com.br/ldglsnwvdt?",
      },
      {
        slotIndex: 3,
        offerHash: "tro3cz1bwi",
        productHash: "bh43xmwvf5",
        checkoutUrl: "https://global.tribopay.com.br/tro3cz1bwi?",
      },
      {
        slotIndex: 4,
        offerHash: "ryepjr46qd",
        productHash: "gfkmqghas2",
        checkoutUrl: "https://global.tribopay.com.br/ryepjr46qd?",
      },
    ],
  },
};

// Helper para obter pacote pelo ID
export function getTribopayPackage(packageId: string): TribopayPackage | undefined {
  return Object.values(TRIBOPAY_PACKAGES).find((pkg) => pkg.id === packageId);
}

// Helper para validar se todos os hashes foram configurados
export function validateTribopayHashes(): { valid: boolean; missing: string[] } {
  const missing: string[] = [];

  Object.entries(TRIBOPAY_PACKAGES).forEach(([key, pkg]) => {
    if (!pkg.offerHash || pkg.offerHash.trim() === "") {
      missing.push(key);
    }
  });

  return {
    valid: missing.length === 0,
    missing,
  };
}

// Lista de todos os pacotes (útil para renderizar)
export const ALL_PACKAGES = Object.values(TRIBOPAY_PACKAGES);
